import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { BookOpen, Building2, CalendarDays, Clock, FolderKanban, GraduationCap, LayoutDashboard, LogOut, Menu, Moon, Palette, Sun, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { ErrorBoundary } from "@/components/States";
import { BrandMark } from "@/components/AuthLayout";
import TrialBanner from "@/components/TrialBanner";
import BetaBanner from "@/components/BetaBanner";
import WalkthroughGate from "@/components/learn/WalkthroughGate";
import ElmMark from "@/components/ElmMark";

const NAV = [
  { to: "/", label: "Today", icon: LayoutDashboard, roles: ["admin", "crew", "client"], testid: "nav-today" },
  { to: "/projects", label: "Projects", icon: FolderKanban, roles: ["admin", "crew"], testid: "nav-projects" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, roles: ["admin", "crew"], testid: "nav-schedule" },
  { group: "Money", to: "/time", label: "Timesheets", icon: Clock, roles: ["admin", "crew"], key: "timesheets", testid: "nav-timesheets" },
  { to: "/price-book", label: "Price Book", icon: BookOpen, roles: ["admin"], key: "price_book", testid: "nav-price-book" },
  { group: "Workspace", to: "/team", label: "People", icon: Users, roles: ["admin"], key: "team", testid: "nav-people" },
  { to: "/workspace", label: "Settings", icon: Palette, roles: ["admin"], testid: "nav-settings" },
  { to: "/learn", label: "Learn", icon: GraduationCap, roles: ["admin", "crew", "client"], testid: "nav-learn" },
  { group: "E.L.M admin", to: "/platform", label: "Platform", icon: Building2, roles: ["admin"], owner: true, testid: "nav-platform" },
];

function useTheme() {
  const [dark, setDark] = useState(() => localStorage.getItem("elm_theme") === "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("elm_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

function NavItems({ user, onNav }) {
  const { workspace } = useAuth();
  const hidden = workspace?.hidden || [];
  const ownerKind = workspace?.kind === "owner_builder";
  const items = NAV.filter((n) => n.roles.includes(user.role) && (!n.owner || user.platform_owner) && !(n.key && hidden.includes(n.key)));
  let lastGroup = null;
  return items.map((n) => {
    const heading = n.group && n.group !== lastGroup ? n.group : null;
    if (n.group) lastGroup = n.group;
    return (
      <div key={n.to}>
        {heading && <p className="mb-1 mt-5 px-3 text-[10px] font-semibold uppercase tracking-wider text-white/40" data-testid={`nav-group-${heading.split(" ")[0].toLowerCase()}`}>{heading}</p>}
        <NavLink to={n.to} end={n.to === "/"} onClick={onNav} data-testid={n.testid}
          className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 ${isActive ? "bg-white/[0.12] text-white" : "text-white/60 hover:bg-white/[0.06] hover:text-white"}`}>
          <n.icon className="h-4 w-4" strokeWidth={1.6} /> {ownerKind && n.ownerLabel ? n.ownerLabel : n.label}
        </NavLink>
      </div>
    );
  });
}

function Brand({ dim = "text-white/55" }) {
  const { brand } = useAuth();
  return (
    <div className="px-3">
      <BrandMark />
      {brand.branding?.tagline && !brand.branding?.logo_url && <p className={`mt-1.5 text-[10px] uppercase tracking-[0.16em] ${dim}`} data-testid="brand-tagline">{brand.branding.tagline}</p>}
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const [dark, setDark] = useTheme();
  const [open, setOpen] = useState(false);
  const footer = (
    <div className="relative z-10 space-y-3 border-t border-white/10 pt-4">
      <div className="px-3">
        <p className="truncate text-sm font-medium" data-testid="current-user-name">{user.name || user.email}</p>
        <p className="text-xs capitalize text-white/55" data-testid="current-user-role">{user.role}</p>
      </div>
      <div className="flex gap-1 px-1">
        <Button variant="ghost" size="sm" className="text-white/80 hover:bg-white/10 hover:text-white" onClick={() => setDark(!dark)} data-testid="theme-toggle-button" aria-label="Toggle theme">
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
        <Button variant="ghost" size="sm" className="text-white/80 hover:bg-white/10 hover:text-white" onClick={logout} data-testid="logout-button"><LogOut className="mr-2 h-4 w-4" />Log out</Button>
      </div>
      <div className="px-3"><ElmMark light /></div>
    </div>
  );
  return (
    <div className="min-h-screen lg:flex">
      <aside className="spruce sticky top-0 hidden h-screen w-64 shrink-0 flex-col justify-between overflow-hidden p-4 py-8 lg:flex">
        <div className="relative z-10 space-y-10"><Brand /><nav className="space-y-0.5"><NavItems user={user} /></nav></div>
        {footer}
      </aside>
      <header className="sticky top-0 z-40 flex items-center justify-between border-b bg-background/80 px-5 py-3 backdrop-blur-md lg:hidden">
        <div className="text-primary"><Brand dim="text-muted-foreground" /></div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><Button variant="ghost" size="icon" data-testid="mobile-menu-button" aria-label="Open menu"><Menu /></Button></SheetTrigger>
          <SheetContent side="left" className="spruce flex w-72 flex-col justify-between border-0 py-10 [&>button]:text-white">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <div className="space-y-8"><Brand /><nav className="space-y-1"><NavItems user={user} onNav={() => setOpen(false)} /></nav></div>
            {footer}
          </SheetContent>
        </Sheet>
      </header>
      <main className="min-w-0 flex-1">
        <BetaBanner />
        {user.role === "admin" && <TrialBanner />}
        <ErrorBoundary><Outlet /></ErrorBoundary>
      </main>
      <WalkthroughGate />
    </div>
  );
}
